import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  RiCloseLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiCalendarLine,
  RiFileList3Line,
  RiUserLine,
  RiArrowDownSLine,
  RiMegaphoneLine,
  RiPaletteLine,
  RiUserStarLine,
  RiPriceTag3Line,
  RiArrowLeftLine,
  RiSaveLine,
  RiRulerLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = process.env.REACT_APP_API_URL;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Label = ({ children, required = false }) => (
  <label className="text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-1 block">
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const inputCls =
  "w-full bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition";

const selectCls =
  "w-full bg-[#3d2626] border border-[#3d2626] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition appearance-none cursor-pointer";

const SelectWrapper = ({ children }) => (
  <div className="relative">
    {children}
    <RiArrowDownSLine className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-600 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
        map[status] || map.pending
      }`}
    >
      {status || "pending"}
    </span>
  );
};

export default function EditQuotation() {
  const { id: quotationId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [quotationNumber, setQuotationNumber] = useState("");
  const [currentStatus, setCurrentStatus] = useState("pending");

  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [globalAdvTypes, setGlobalAdvTypes] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [rateCardsLoading, setRateCardsLoading] = useState(false);

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (msg, type = "success") => setToast({ message: msg, type });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ── Load quotation + reference data ───────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setPageLoading(true);
      try {
        const [cR, aR, atR, qR] = await Promise.allSettled([
          axios.get(`${BASE_URL}/bank/get-banks`),
          axios.get(`${BASE_URL}/agent/all-agents`),
          axios.get(`${BASE_URL}/quotation-advertising-type`),
          axios.get(`${BASE_URL}/quotation/get-by-id/${quotationId}`),
        ]);

        if (cR.status === "fulfilled")
          setClients(cR.value?.data?.data || cR.value.data || []);
        if (aR.status === "fulfilled")
          setAgents(aR.value?.data?.data || aR.value.data || []);
        if (atR.status === "fulfilled")
          setGlobalAdvTypes(atR.value?.data?.data || atR.value.data || []);

        if (qR.status === "fulfilled") {
          const q = qR.value?.data?.data || qR.value.data;

          setQuotationNumber(q.quotationNumber || "");
          setCurrentStatus(q.status || "pending");

          const advTypeId = q.advertisingType?._id || q.advertisingType || "";

          // Map existing mediaRateCardItems — populated objects have ._id inside .mediaRateCard
          const existingItems = (q.mediaRateCardItems || []).map((item) => ({
            mediaRateCard: item.mediaRateCard?._id || item.mediaRateCard,
            height: item.height || 1,
            width: item.width || 1,
          }));

          setForm({
            quotationType: q.quotationType || "advertising",
            personName: q.personName || "",
            advertisingType: advTypeId,
            mediaRateCardItems: existingItems,
            client: q.client?._id || q.client || "",
            referral: q.referral?._id || q.referral || "",
            date: q.date
              ? new Date(q.date).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0],
          });

          // Pre-fetch rate cards for the existing advertisingType
          if (advTypeId && q.quotationType === "advertising") {
            try {
              setRateCardsLoading(true);
              const res = await axios.get(
                `${BASE_URL}/media-rate/by-advertising-type/${advTypeId}`,
              );
              setRateCards(res.data?.data || []);
            } catch {
              notify("Failed to load rate cards", "error");
            } finally {
              setRateCardsLoading(false);
            }
          }
        }
      } catch {
        notify("Failed to load quotation", "error");
      } finally {
        setPageLoading(false);
      }
    };

    if (quotationId) load();
  }, [quotationId]);

  // ── Re-fetch rate cards when advertisingType changes ──────────────────────
  useEffect(() => {
    if (!form) return;
    if (!form.advertisingType || form.quotationType !== "advertising") {
      setRateCards([]);
      return;
    }

    const fetchRateCards = async () => {
      setRateCardsLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/media-rate/by-advertising-type/${form.advertisingType}`,
        );
        setRateCards(res.data?.data || []);
      } catch {
        notify("Failed to load rate cards", "error");
        setRateCards([]);
      } finally {
        setRateCardsLoading(false);
      }
    };

    fetchRateCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.advertisingType, form?.quotationType]);

  // ── Rate card helpers ──────────────────────────────────────────────────────
  const isSelected = (rateCardId) =>
    form.mediaRateCardItems.some((item) => item.mediaRateCard === rateCardId);

  const getSelectedItem = (rateCardId) =>
    form.mediaRateCardItems.find((item) => item.mediaRateCard === rateCardId);

  const getRateCardById = (id) => rateCards.find((rc) => rc._id === id);

  const handleRateCardToggle = (rc) => {
    const alreadySelected = isSelected(rc._id);
    if (alreadySelected) {
      setField(
        "mediaRateCardItems",
        form.mediaRateCardItems.filter((item) => item.mediaRateCard !== rc._id),
      );
    } else {
      setField("mediaRateCardItems", [
        ...form.mediaRateCardItems,
        { mediaRateCard: rc._id, height: 1, width: 1 },
      ]);
    }
  };

  const handleDimensionChange = (rateCardId, field, value) => {
    setField(
      "mediaRateCardItems",
      form.mediaRateCardItems.map((item) =>
        item.mediaRateCard === rateCardId
          ? { ...item, [field]: parseFloat(value) || 0 }
          : item,
      ),
    );
  };

  const handleSelectAll = () => {
    if (form.mediaRateCardItems.length === rateCards.length) {
      setField("mediaRateCardItems", []);
    } else {
      setField(
        "mediaRateCardItems",
        rateCards.map((rc) => ({ mediaRateCard: rc._id, height: 1, width: 1 })),
      );
    }
  };

  const calculatePreview = (height, width, ratePerSquareMeter) => {
    const area = height * width;
    const amount = area * ratePerSquareMeter;
    return { area, amount };
  };

  const calculateTotalAmount = () => {
    let total = 0;
    form.mediaRateCardItems.forEach((item) => {
      const rc = getRateCardById(item.mediaRateCard);
      if (rc) total += item.height * item.width * rc.ratePerSquareMeter;
    });
    return total;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.client) return notify("Please select a client", "error");

    if (form.quotationType === "advertising") {
      if (!form.personName.trim())
        return notify(
          "Person name is required for advertising quotation",
          "error",
        );
      if (!form.advertisingType)
        return notify(
          "Advertising type is required for advertising quotation",
          "error",
        );
      if (!form?.mediaRateCardItems?.length)
        return notify(
          "At least one media rate card is required for advertising quotation",
          "error",
        );

      for (let i = 0; i < form?.mediaRateCardItems?.length; i++) {
        const item = form.mediaRateCardItems[i];
        if (!item.height || item.height <= 0)
          return notify(
            "Valid height is required for all selected rate cards",
            "error",
          );
        if (!item.width || item.width <= 0)
          return notify(
            "Valid width is required for all selected rate cards",
            "error",
          );
      }
    }

    setSaving(true);
    try {
      const payload = {
        quotationType: form.quotationType,
        client: form.client,
        date: form.date,
        referral: form.referral || null,
      };

      if (form.quotationType === "advertising") {
        payload.personName = form.personName;
        payload.advertisingType = form.advertisingType;
        payload.mediaRateCardItems = form.mediaRateCardItems;
      }

      await axios.put(`${BASE_URL}/quotation/update/${quotationId}`, payload);
      notify("Quotation updated successfully!");
      setTimeout(() => navigate(-1), 1200);
    } catch (err) {
      notify(
        err.response?.data?.message || "Failed to update quotation",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Status update ──────────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await axios.patch(`${BASE_URL}/quotation/${quotationId}/status`, {
        status: newStatus,
      });
      setCurrentStatus(newStatus);
      notify(`Status changed to ${newStatus}`);
    } catch {
      notify("Failed to update status", "error");
    } finally {
      setStatusUpdating(false);
    }
  };

  // ── Loading / not-found states ─────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-blue-400">
          <Spinner className="w-8 h-8 text-indigo-500" />
          <span className="text-sm">Loading quotation…</span>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 text-sm mb-3">Quotation not found.</p>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-indigo-600 hover:underline"
          >
            ← Back to list
          </button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-500 hover:bg-blue-50 transition"
              title="Back to list"
            >
              <RiArrowLeftLine className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-md shadow-indigo-200">
              <RiFileList3Line className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-800 tracking-tight flex items-center gap-2">
                Edit Quotation
                {quotationNumber && (
                  <span className="font-mono text-indigo-600 text-sm">
                    — {quotationNumber}
                  </span>
                )}
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Modify & Save
              </p>
            </div>
          </div>

          {/* Status control */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                Status:
              </span>
              <StatusBadge status={currentStatus} />
            </div>
            <div className="flex gap-1.5">
              {["pending", "approved", "rejected"]
                .filter((s) => s !== currentStatus)
                .map((s) => (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(s)}
                    disabled={statusUpdating}
                    className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition disabled:opacity-50
                      ${
                        s === "approved"
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                          : s === "rejected"
                            ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100"
                            : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                      }`}
                  >
                    {statusUpdating ? (
                      <Spinner className="w-3 h-3 inline" />
                    ) : null}{" "}
                    Mark {s}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 py-6 pb-16 space-y-5 w-full mx-auto">
        {/* ── Quotation Details ── */}
        <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
              Quotation Details
            </span>
          </div>

          <div className="p-5 grid grid-cols-2 gap-5 sm:grid-cols-3">
            {/* Quotation Type */}
            <div className="col-span-2 sm:col-span-1">
              <Label>Quotation Type</Label>
              <div className="flex gap-2">
                {[
                  {
                    value: "advertising",
                    label: "Advertising",
                    Icon: RiMegaphoneLine,
                  },
                  { value: "branding", label: "Branding", Icon: RiPaletteLine },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({
                        ...f,
                        quotationType: value,
                        ...(value === "branding" && {
                          personName: "",
                          advertisingType: "",
                          mediaRateCardItems: [],
                        }),
                      }));
                      if (value === "branding") setRateCards([]);
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition
                      ${
                        form.quotationType === value
                          ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                          : "bg-blue-50 border-blue-200 text-blue-500 hover:bg-blue-100"
                      }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Client */}
            <div>
              <Label required>Client</Label>
              <SelectWrapper>
                <select
                  value={form.client}
                  onChange={(e) => setField("client", e.target.value)}
                  className={selectCls}
                >
                  <option value="">-- Select Client --</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.bankName || c.name}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </div>

            {/* Date */}
            <div>
              <Label>Date</Label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setField("date", e.target.value)}
                  className={`${inputCls} pl-9`}
                />
              </div>
            </div>

            {/* Person Name — advertising only */}
            {form.quotationType === "advertising" && (
              <div>
                <Label required>Person Name</Label>
                <div className="relative">
                  <RiUserLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                  <input
                    type="text"
                    value={form.personName}
                    onChange={(e) => setField("personName", e.target.value)}
                    placeholder="Enter person name"
                    className={`${inputCls} pl-9`}
                  />
                </div>
              </div>
            )}

            {/* Advertising Type — advertising only */}
            {form.quotationType === "advertising" && (
              <div>
                <Label required>
                  <RiPriceTag3Line className="inline w-3 h-3 mr-1" />
                  Advertising Type
                </Label>
                <SelectWrapper>
                  <select
                    value={form.advertisingType}
                    onChange={(e) => {
                      setField("advertisingType", e.target.value);
                      setField("mediaRateCardItems", []);
                    }}
                    className={selectCls}
                  >
                    <option value="">-- Select Type --</option>
                    {globalAdvTypes.map((at) => (
                      <option key={at._id} value={at._id}>
                        {at.name}
                      </option>
                    ))}
                  </select>
                </SelectWrapper>
              </div>
            )}

            {/* Referral (Agent) */}
            <div>
              <Label>
                <RiUserStarLine className="inline w-3 h-3 mr-1" />
                Referral (Agent)
              </Label>
              <SelectWrapper>
                <select
                  value={form.referral}
                  onChange={(e) => setField("referral", e.target.value)}
                  className={selectCls}
                >
                  <option value="">-- Select Agent --</option>
                  {agents.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </div>
          </div>
        </div>

        {/* ── Media Rate Cards Section ── */}
        {form.quotationType === "advertising" && form.advertisingType && (
          <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Media Rate Cards
              </span>
              {rateCards.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700 px-3 py-1 rounded-lg hover:bg-indigo-50 transition"
                >
                  {form?.mediaRateCardItems?.length === rateCards.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              )}
            </div>

            <div className="p-5">
              {rateCardsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="w-6 h-6 text-blue-400" />
                  <span className="ml-2 text-sm text-blue-500">
                    Loading rate cards…
                  </span>
                </div>
              ) : rateCards.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No rate cards available for this advertising type
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {rateCards.map((rc) => {
                    const selected = isSelected(rc._id);
                    const selectedItem = getSelectedItem(rc._id);
                    const { area, amount } =
                      selected && selectedItem
                        ? calculatePreview(
                            selectedItem.height,
                            selectedItem.width,
                            rc.ratePerSquareMeter,
                          )
                        : { area: 0, amount: 0 };

                    return (
                      <div
                        key={rc._id}
                        className={`border rounded-lg transition ${
                          selected
                            ? "border-indigo-300 bg-indigo-50/30"
                            : "border-blue-100 hover:bg-blue-50/50"
                        }`}
                      >
                        {/* Rate Card Header */}
                        <div className="p-3">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => handleRateCardToggle(rc)}
                              className="mt-0.5 w-4 h-4 text-indigo-600 rounded border-blue-300 focus:ring-indigo-500"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-sm text-slate-800">
                                  {rc.media?.organisation ?? "Unknown Media"}
                                </span>
                                <span className="text-xs text-blue-500 bg-blue-100 px-2 py-0.5 rounded">
                                  {rc.edition}
                                </span>
                                <span className="text-xs font-mono text-slate-500">
                                  Rate: ₹
                                  {rc.ratePerSquareMeter?.toLocaleString(
                                    "en-IN",
                                  )}
                                  /sqm
                                </span>
                              </div>
                              {rc.description && (
                                <p className="text-xs text-slate-400 mt-1">
                                  {rc.description}
                                </p>
                              )}
                            </div>
                          </label>
                        </div>

                        {/* Dimension Inputs */}
                        {selected && selectedItem && (
                          <div className="px-3 pb-3 pt-0 border-t border-indigo-100 mt-2">
                            <div className="grid grid-cols-2 gap-3 pt-3">
                              <div>
                                <label className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">
                                  Height (m)
                                </label>
                                <div className="relative mt-1">
                                  <RiRulerLine className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-indigo-400" />
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={selectedItem.height}
                                    onChange={(e) =>
                                      handleDimensionChange(
                                        rc._id,
                                        "height",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Height in meters"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">
                                  Width (m)
                                </label>
                                <div className="relative mt-1">
                                  <RiRulerLine className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-indigo-400" />
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={selectedItem.width}
                                    onChange={(e) =>
                                      handleDimensionChange(
                                        rc._id,
                                        "width",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Width in meters"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Preview Calculation */}
                            {area > 0 && amount > 0 && (
                              <div className="mt-3 pt-2 border-t border-indigo-100">
                                <div className="flex justify-between text-xs">
                                  <span className="text-slate-500">
                                    Area: {area.toFixed(2)} m²
                                  </span>
                                  <span className="font-semibold text-indigo-600">
                                    Amount: ₹
                                    {amount.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Summary */}
              {form?.mediaRateCardItems?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-blue-100">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">
                      Selected {form?.mediaRateCardItems?.length} of{" "}
                      {rateCards?.length} rate cards
                    </span>
                    <span className="text-sm font-bold text-indigo-600">
                      Total: ₹
                      {calculateTotalAmount().toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Actions ── */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 font-semibold transition"
          >
            <RiArrowLeftLine className="w-4 h-4" /> Back
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white transition shadow-lg shadow-indigo-200 disabled:opacity-60"
          >
            {saving ? <Spinner /> : <RiSaveLine className="w-4 h-4" />}
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
